/**
 * The strip along the bottom of the Overview map — **data insights, one at a time**.
 *
 * It used to render `/audit/ticker`, which is what the client saw and objected to on 8 Sep
 * 2026: *"appears to currently communicate status of backend processes… We suggest this
 * communicates or highlights project or data insights dynamically, but note that such
 * insights should already exist at the Insights panel for follow-up."* So the strip no
 * longer fetches anything. Its lines are generated by `tickerInsights` from the very memos
 * that paint the panel beside it — the metric cards, the extremes, the pathway chart, the
 * map key — which is what makes every line followable: the number in the strip is the number
 * in the panel, because it is the same value.
 *
 * `/audit/ticker` is untouched and still serves the admin surface; nothing on this page calls
 * it any more. That was the smaller of the two changes item 8 offered (a client-side
 * generator against a new backend endpoint), and it is the one with no server work at all.
 *
 * The rotation is the older component's and unchanged: **one line at a time**, because four
 * `<span>`s under `white-space:nowrap; overflow:hidden` is not a ticker, it is a sentence
 * that runs out of room. Nothing here is `dangerouslySetInnerHTML`: the keys are ours and the
 * numbers are formatted, so this renders text.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { insightParams } from './overview';
import type { TickerInsight } from './overview';

/** How long each line is shown. Long enough to read a short sentence twice. */
export const TICKER_INTERVAL_MS = 6000;

export interface TickerProps {
  /** The rotation, in order. Empty renders nothing rather than an empty pill. */
  insights: readonly TickerInsight[];
}

export function Ticker({ insights }: TickerProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Nothing to rotate through, and — more to the point — no interval left running behind
    // a closed splash. The whole component unmounts with the overlay, but the splash is
    // reopened from the topbar and a leaked timer per open would accumulate.
    if (insights.length < 2) return;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % insights.length),
      TICKER_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [insights.length]);

  // The rotation index outlives the array it indexes: a filter change regenerates the lines
  // and the new array may be shorter. Clamping at read time is one line; a `useEffect` that
  // reset it would render one frame of nothing first.
  const current = insights[index % Math.max(insights.length, 1)];
  if (!current) return null;

  /**
   * One `t()` per noun, then one for the sentence.
   *
   * i18next chooses a single plural form per call from a single `count`, so a sentence that
   * governs two nouns had to get one of them wrong: with 171 activities in one basin the
   * line read *"171 activities mapped across 1 intervention watersheds"*, and the coverage
   * line — three nouns and no `count` at all — read *"1 watersheds and 3 partnerships across
   * 1 countries"*. Each fragment is now pluralised against its own number here, and the
   * sentence interpolates the finished noun phrases. The generator stays language-free: it
   * hands over keys and numbers, one noun at a time (`TickerInsight.fragments`).
   */
  const params = insightParams(
    current,
    (key, count, value) => t(`overview.ticker.frag.${key}`, { count, value }),
    // The `terms` slot is inert today: the only statements that ever carried one were the
    // target sentences, which the client removed on 2026-09-09 along with their keys. Kept
    // wired because the slot is `TickerInsight`'s, not the target generator's.
    (key) => t(`overview.ticker.term.${key}`),
  );

  return (
    <div
      id="hpTicker"
      className="glass"
      data-testid="splash-ticker"
      data-insight={current.key}
      /* The content changes on a timer nobody asked for, so it is announced politely or
         not at all — `polite` waits for a screen reader to finish what it is saying. */
      aria-live="polite"
      aria-atomic="true"
    >
      <b>{t('overview.ticker.label')}</b>
      <span className="hp-tick-text">{t(`overview.ticker.${current.key}`, params)}</span>
    </div>
  );
}

export default Ticker;
